import {headOption} from 'utils/common';
import {
  createFetchActionForNamespace,
  createFetchPaginatedActionForNamespace,
  createPostActionForNamespace,
  createReducer,
  createFetchReducers,
  createFetchReducersWithPagination,
  createFetchSelectors,
  noDataForAction
} from 'utils/reducers';

import {transformFiltersForStoreToFiltersForApi} from 'utils/filters';
import {transformPaginationForStoreToPaginationForApi} from 'utils/pagination';
import {transformSortForStoreToSortForUrl} from 'utils/sort';
import {selectSort, SORTING} from 'ducks/sorting';
import {selectFilters, FILTERS} from 'ducks/filters';
import {selectPagination, PAGINATION} from 'ducks/pagination';

const createFetchAction = createFetchActionForNamespace('bookings/list');
const createPostAction = createPostActionForNamespace('bookings/list');
const createFetchActionWithPagination = createFetchPaginatedActionForNamespace(
  'bookings/list'
);

export const fetchBookings = createFetchActionWithPagination({
  name: 'FETCH_BOOKINGS',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {
      locationRef,
      extraActions
    }
  }),
  stateKey: 'bookings',
  defaultStateValue: [],
  extraGetParamsSelectors: [
    state =>
      transformFiltersForStoreToFiltersForApi(
        selectFilters(state, FILTERS.BOOKING_LIST)
      ),
    state =>
      transformPaginationForStoreToPaginationForApi(
        selectPagination(state, PAGINATION.BOOKING_LIST)
      ),
    state =>
      transformSortForStoreToSortForUrl(selectSort(state, SORTING.BOOKING_LIST))
  ],
  paginationNamespace: PAGINATION.BOOKING_LIST
});

const fetchBookingsSelector = createFetchSelectors(fetchBookings);
export const selectBookings = fetchBookingsSelector.data;
export const selectBookingsLoading = fetchBookingsSelector.isLoading;

export const fetchBookingTypes = createFetchAction({
  name: 'FETCH_BOOKING_TYPES',
  initialAction: noDataForAction,
  stateKey: 'types',
  defaultStateValue: []
});

export const selectBookingTypes = createFetchSelectors(fetchBookingTypes).data;
export const selectDefaultBookingType = state =>
  headOption(selectBookingTypes(state).filter(bt => bt.default));

export const fetchBookingOrganisations = createFetchAction({
  name: 'FETCH_BOOKING_ORGANISATIONS',
  initialAction: noDataForAction,
  stateKey: 'organisations',
  defaultStateValue: []
});

export const selectBookingOrganisations = createFetchSelectors(
  fetchBookingOrganisations
).data;

export const fetchBookingListFilters = createFetchAction({
  name: 'FETCH_BOOKING_LIST_FILTERS',
  initialAction: ({locationRef}) => ({payload: {locationRef}}),
  stateKey: 'bookingListFilters',
  defaultStateValue: {
    bookingStatusFilters: [],
    roomTypeFilters: [],
    createdByFilters: []
  }
});

export const fetchSalesAgents = createFetchAction({
  name: 'FETCH_SALES_AGENT',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {locationRef, extraActions}
  }),
  stateKey: 'salesAgents',
  defaultStateValue: []
});

export const selectSalesAgent = createFetchSelectors(fetchSalesAgents).data;

export const selectBookingsFilters = createFetchSelectors(
  fetchBookingListFilters
).data;

export const fetchGoCardlessMandateStatuses = createFetchAction({
  name: 'FETCH_GOCARDLESS_MANDATE_STATUSES',
  initialAction: noDataForAction,
  stateKey: 'goCardlessMandateStatuses',
  defaultStateValue: []
});

export const selectGoCardlessMandateStatuses = createFetchSelectors(
  fetchGoCardlessMandateStatuses
).data;

export const fetchGoCardlessInvitationStatuses = createFetchAction({
  name: 'FETCH_GOCARDLESS_INVITATION_STATUSES',
  initialAction: noDataForAction,
  stateKey: 'goCardlessInvitationStatuses',
  defaultStateValue: []
});

export const selectGoCardlessInvitationStatuses = createFetchSelectors(
  fetchGoCardlessInvitationStatuses
).data;

export const createBooking = createPostAction({
  name: 'CREATE_BOOKING',
  initialAction: (
    {
      locationRef,
      roomType,
      primaryTenant,
      secondaryTenant,
      pwRent,
      roomDeposit,
      startDate,
      endDate,
      room,
      createCleanupPeriod,
      cleanupPeriodDays,
      bookingType,
      bookingOrganisation,
      bookingSet
    },
    extraActions = []
  ) => ({
    payload: {
      locationRef,
      roomType,
      primaryTenant,
      secondaryTenant,
      pwRent,
      roomDeposit,
      startDate,
      endDate,
      room,
      createCleanupPeriod,
      cleanupPeriodDays,
      bookingType,
      bookingOrganisation,
      bookingSet,
      extraActions
    }
  }),
  formName: 'createBooking'
});

export const createBookingExtension = createPostAction({
  name: 'CREATE_BOOKING_EXTENSION',
  initialAction: (
    {
      existingBooking,
      room,
      roomType,
      startDate,
      endDate,
      primaryTenant,
      secondaryTenant,
      pwRent,
      roomDeposit,
      createCleanupPeriod,
      cleanupPeriodDays
    },
    extraActions = []
  ) => ({
    payload: {
      existingBooking,
      room,
      roomType,
      startDate,
      endDate,
      primaryTenant,
      secondaryTenant,
      pwRent,
      roomDeposit,
      createCleanupPeriod,
      cleanupPeriodDays,
      extraActions
    }
  }),
  formName: 'createBookingExtension'
});

export const BookingStatusesDescriptions = [
  {
    label: 'Reserved',
    description:
      'Booking has been created and member verification is in progress.'
  },
  {
    label: 'Qualified',
    description:
      'Required documents and information has been submitted by the member and verified'
  },
  {
    label: 'Cancelled',
    description: 'Booking was cancelled before the agreement was completed'
  },
  {
    label: 'Confirmed',
    description: 'Agreement has been completed'
  },
  {
    label: 'Due in',
    description: 'Member is due to check in today'
  },
  {
    label: 'In house',
    description:
      'Member status throughout their stay in the building. It starts the day after they have checked in, and ends on the day member is due to check out.'
  },
  {
    label: 'No show',
    description:
      'Booking started before today but member has not checked in yet'
  },
  {
    label: 'Due out',
    description: 'Member is due to check out today'
  },
  {
    label: 'Checked out',
    description: 'Member checked out today'
  },
  {
    label: 'Overstay',
    description: 'Member has overstayed their booking'
  }
];

const initialState = {
  error: null // TODO: is this used and for what?
};

const fetchReducers = createReducer(initialState, {
  ...createFetchReducersWithPagination(fetchBookings),
  ...createFetchReducers(fetchBookingListFilters),
  ...createFetchReducers(fetchBookingTypes),
  ...createFetchReducers(fetchBookingOrganisations),
  ...createFetchReducers(fetchSalesAgents),
  ...createFetchReducers(fetchGoCardlessMandateStatuses),
  ...createFetchReducers(fetchGoCardlessInvitationStatuses)
});

const reducer = (state = initialState, action) => {
  const newState = fetchReducers(state, action);

  switch (action.type) {
    default:
      return newState;
  }
};

export default reducer;

export {default as fetchBookingsSaga} from './saga';
